import { useEffect } from "react";
import { takeUndo } from "src/utils/lastUndo";

// Keys that work on every page, and the list "?" shows. Page-specific keys
// (Cases, a case, its documents) live with those pages; keep this list in
// step with them.
export const SHORTCUTS = [
  {
    group: "Everywhere",
    keys: [
      { keys: ["Ctrl", "K"], mac: ["⌘", "K"], does: "Search: a case number, a name, a page or a word" },
      { keys: ["?"], does: "Show this list" },
      { keys: ["Esc"], does: "Close a dialog or menu" },
      { keys: ["Ctrl", "Enter"], mac: ["⌘", "Enter"], does: "Save the open dialog" },
      { keys: ["Enter"], does: "In a dialog with one box: save it" },
      { keys: ["Ctrl", "Z"], mac: ["⌘", "Z"], does: "Undo what was just deleted or changed (for a minute, outside a text box)" },
    ],
  },
  {
    group: "Cases",
    keys: [
      { keys: ["/"], does: "Search the cases" },
      { keys: ["j"], does: "Next case in the list" },
      { keys: ["k"], does: "Previous case in the list" },
      { keys: ["Enter"], does: "Open the chosen case" },
    ],
  },
  {
    group: "A case",
    keys: [
      { keys: ["j"], does: "Next case" },
      { keys: ["k"], does: "Previous case" },
      { keys: ["u"], does: "Back to the list" },
    ],
  },
  {
    group: "A case’s documents (on a document’s row)",
    keys: [
      { keys: ["V"], does: "View the document" },
      { keys: ["↑"], does: "Previous document" },
      { keys: ["↓"], does: "Next document" },
      { keys: ["A"], does: "Approve it (only once “Letter shortcuts A and N” is on)" },
      { keys: ["N"], does: "Ask for a new copy (only once “Letter shortcuts A and N” is on)" },
    ],
  },
];

const TEXT_INPUT = 'input:not([type]), input[type="text"], input[type="email"], input[type="number"], input[type="search"], input[type="tel"], input[type="url"], input[type="password"]';

// Someone is typing: keys belong to the field, not to the page.
export function isTypingTarget(target) {
  if (!(target instanceof Element)) return false;
  return !!target.closest('input, textarea, select, [contenteditable="true"], [role="combobox"], [role="textbox"]');
}

// The dialog's main button: one marked data-primary, else the last filled
// button in its actions that is enabled and isn't a red (destructive) one.
// A keyboard shortcut never confirms a delete.
export function primaryButton(dialog) {
  if (!dialog) return null;
  const marked = dialog.querySelector('[data-primary="true"]:not([disabled])');
  if (marked) return marked.classList.contains("MuiButton-containedError") ? null : marked;
  const actions = dialog.querySelectorAll(".MuiDialogActions-root button.MuiButton-contained:not([disabled]):not(.Mui-disabled)");
  const last = actions[actions.length - 1];
  return last && !last.classList.contains("MuiButton-containedError") ? last : null;
}

const openDialog = (target) => (target instanceof Element ? target.closest(".MuiDialog-root") : null);

const visible = (el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);

// Ctrl/⌘ + Enter saves the dialog the focus is in. Enter saves a dialog
// whose only field is one line of text (a name, an amount), unless a form
// already does that or a list of suggestions is open.
export function dialogKey(event) {
  if (event.defaultPrevented || event.key !== "Enter" || event.altKey || event.shiftKey) return false;
  const dialog = openDialog(event.target);
  if (!dialog) return false;
  const withModifier = event.ctrlKey || event.metaKey;
  if (!withModifier) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches(TEXT_INPUT) || input.form) return false;
    if (input.getAttribute("aria-autocomplete") || input.closest('[role="combobox"]') || input.getAttribute("aria-expanded") === "true") return false;
    const fields = [...dialog.querySelectorAll(`${TEXT_INPUT}, textarea, select, [contenteditable="true"]`)].filter(visible);
    if (fields.length !== 1) return false;
  }
  const button = primaryButton(dialog);
  if (!button) return false;
  event.preventDefault();
  button.click();
  return true;
}

// When a dialog opens, the keyboard starts in its first field (not on the
// dialog's frame), unless something inside already has the focus.
export function focusFirstField(node) {
  if (!(node instanceof Element)) return;
  // Not on a touch screen: a focused field opens the on-screen keyboard
  // over the dialog before the person has read it.
  try {
    if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) return;
  } catch {
    /* no media queries: carry on */
  }
  const active = document.activeElement;
  if (active && node.contains(active) && active.matches("input, textarea, select, button, [contenteditable='true'], [role='radio'], [role='switch']")) return;
  const first = [...node.querySelectorAll(`${TEXT_INPUT}, textarea, select, [contenteditable="true"]`)].find(
    (el) => visible(el) && !el.disabled && !el.readOnly && el.getAttribute("aria-hidden") !== "true",
  );
  if (first) first.focus();
}

// The keys every page shares: "?" for the list, and the dialog keys.
export function useGlobalKeys({ onShortcuts }) {
  useEffect(() => {
    const onKey = (event) => {
      if (dialogKey(event)) return;
      // Ctrl+Z / ⌘Z outside a text box: the newest Undo on screen.
      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey && String(event.key).toLowerCase() === "z" && !isTypingTarget(event.target)) {
        const undo = takeUndo();
        if (undo) {
          event.preventDefault();
          undo.run();
        }
        return;
      }
      if (event.key === "?" && !event.ctrlKey && !event.metaKey && !event.altKey && !isTypingTarget(event.target) && !openDialog(event.target)) {
        if (document.querySelector(".MuiDialog-root")) return;
        event.preventDefault();
        onShortcuts();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onShortcuts]);
}
